(function () {
  if (window.__dyRecommendationLoaded) {
    return;
  }

  window.__dyRecommendationLoaded = true;

  class DyRecommendation extends HTMLElement {
    constructor() {
      super();
      this.flickity = null;
      this.slideshow = null;
      this.isUpdating = false;
      this.titleContainer = null;
      this.titleNode = null;
      this.reinitTimer = null;
      this.totalCardCount = 0;
      this.loadedCardCount = 0;
      this.onRemoteCardLoaded = this.onRemoteCardLoaded.bind(this);
    }

    static get observedAttributes() {
      return ["title"];
    }

    connectedCallback() {
      // Escape hatch: listeners may rewrite the injected DY markup (children,
      // attributes) before any slider/title initialization happens.
      this.dispatchEvent(
        new CustomEvent("dy-recommendation:before-init", {
          bubbles: true,
          detail: { element: this },
        })
      );

      this.adoptHostConfig();
      this.ensureMarkup();
      this.renderTitle();
      this.refreshSlides();
      this.hoistQuickShopModals(this);
      this.observeChildren();
      this.observeAppSwatches();
      this.addEventListener(
        "remote-product-card:loaded",
        this.onRemoteCardLoaded
      );
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.isConnected || oldValue === newValue) {
        return;
      }

      if (name === "title") {
        this.renderTitle();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.swatchObserver) {
        this.swatchObserver.disconnect();
        this.swatchObserver = null;
      }

      if (this.swatchResizeObserver) {
        this.swatchResizeObserver.disconnect();
        this.swatchResizeObserver = null;
      }

      if (this.sliderResizeTimer) {
        window.clearTimeout(this.sliderResizeTimer);
        this.sliderResizeTimer = null;
      }

      if (this.reinitTimer) {
        window.clearTimeout(this.reinitTimer);
        this.reinitTimer = null;
      }

      this.removeEventListener(
        "remote-product-card:loaded",
        this.onRemoteCardLoaded
      );

      if (this.flickity && typeof this.flickity.destroy === "function") {
        this.flickity.destroy();
      }

      this.flickity = null;
    }

    // Copy variant config (theme section settings) from the campaign
    // target onto this element. By default DY-template attributes win;
    // when the target sets data-dy-enforce="true" (section setting),
    // template attributes are stripped so the settings always apply.
    adoptHostConfig() {
      var host = this.closest(".dy-campaign__target");
      if (!host) {
        return;
      }

      var cfg = host.dataset;

      if (cfg.dyEnforce === "true") {
        this.removeAttribute("data-cards");
        this.removeAttribute("data-heading-style");
        this.removeAttribute("controls");
        delete this.dataset.controls;
      }

      if (cfg.dyCards && cfg.dyCards !== "5" && !this.hasAttribute("data-cards")) {
        this.setAttribute("data-cards", cfg.dyCards);
      }

      if (
        cfg.dyHeadingStyle &&
        cfg.dyHeadingStyle !== "standard" &&
        !this.hasAttribute("data-heading-style")
      ) {
        this.setAttribute("data-heading-style", cfg.dyHeadingStyle);
      }

      if (
        cfg.dyControls &&
        !this.getAttribute("controls") &&
        !this.dataset.controls
      ) {
        this.dataset.controls = cfg.dyControls;
      }
    }

    ensureMarkup() {
      if (this.slideshow) {
        return;
      }

      var pageWidth = document.createElement("div");
      pageWidth.className = "page-width page-width--flush-small";

      var gridOverflow = document.createElement("div");
      gridOverflow.className = "grid-overflow-wrapper";

      var recommendations = document.createElement("div");
      recommendations.className = "product-recommendations page-width";

      var slider = document.createElement("div");
      slider.className = "product-single__related";
      slider.setAttribute("data-aos", "overflow__animation");
      slider.setAttribute("data-slideshow", "");

      recommendations.appendChild(slider);
      gridOverflow.appendChild(recommendations);
      pageWidth.appendChild(gridOverflow);
      this.appendChild(pageWidth);

      this.slideshow = slider;
    }

    renderTitle() {
      var title = (this.getAttribute("title") || "").trim();

      if (!title) {
        if (this.titleContainer) {
          this.titleContainer.remove();
          this.titleContainer = null;
          this.titleNode = null;
        }

        return;
      }

      if (!this.titleContainer) {
        var pageWidth = document.createElement("div");
        pageWidth.className = "page-width";

        var header = document.createElement("header");
        header.className = "section-header";

        var heading = document.createElement("h3");
        heading.className = "section-header__title";

        header.appendChild(heading);
        pageWidth.appendChild(header);

        this.insertBefore(pageWidth, this.firstChild);
        this.titleContainer = pageWidth;
        this.titleNode = heading;
      }

      this.titleNode.textContent = title;
    }

    // Variant King injects its swatch element after the card image (its
    // global "After image" placement, shared with Boost/collections).
    // Relocate it to the end of the card content, after the reviews row.
    observeAppSwatches() {
      if (this.swatchObserver) {
        return;
      }

      var host = this.closest(".dy-campaign__target");
      if (!host || host.dataset.dySwatches !== "true") {
        return;
      }

      var self = this;

      // Tracks swatch elements already wired up. WeakSet: entries are
      // garbage-collected with the nodes, nothing to clean up manually.
      var tracked = new WeakSet();

      if (window.ResizeObserver && !this.swatchResizeObserver) {
        // One shared observer for all swatch elements; catches the app
        // lazily populating them (height growth) after insertion.
        this.swatchResizeObserver = new ResizeObserver(function () {
          self.scheduleSliderResize();
        });
      }

      var relocate = function (node) {
        var card = node.closest ? node.closest("remote-product-card") : null;
        if (!card) {
          return;
        }

        var content = card.querySelector(".grid-product__content");
        if (!content) {
          return;
        }

        var reviews = content.querySelector(".grid-product__reviews");
        var moved = false;

        if (reviews) {
          if (reviews.nextElementSibling !== node) {
            reviews.after(node);
            moved = true;
          }
        } else if (content.lastElementChild !== node) {
          content.appendChild(node);
          moved = true;
        }

        // Drain the records our own move just produced so the observer
        // callback never re-processes self-inflicted mutations.
        if (moved && self.swatchObserver) {
          self.swatchObserver.takeRecords();
        }

        if (tracked.has(node)) {
          return;
        }
        tracked.add(node);

        // Observe the whole card, not just the swatch element: any late
        // growth inside it (swatch population, stars, fonts) changes the
        // card's flow-root box and triggers a height recalc.
        if (self.swatchResizeObserver && !tracked.has(card)) {
          tracked.add(card);
          self.swatchResizeObserver.observe(card);
        }

        // Swatches add height after Flickity measured the cells — recalc.
        self.scheduleSliderResize();
      };

      this.swatchObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;

          for (var j = 0; j < added.length; j++) {
            var node = added[j];

            if (node.nodeType !== 1) {
              continue;
            }

            if (node.tagName === "VARIANT-KING-COMBINED-LISTING") {
              relocate(node);
            } else if (node.querySelectorAll) {
              var found = node.querySelectorAll("variant-king-combined-listing");

              for (var k = 0; k < found.length; k++) {
                relocate(found[k]);
              }
            }
          }
        }
      });

      this.swatchObserver.observe(this, { childList: true, subtree: true });

      this.querySelectorAll("variant-king-combined-listing").forEach(relocate);
    }

    // Debounced Flickity resize: swatch injection/population can land in
    // bursts, one recalc after the burst settles is enough.
    scheduleSliderResize() {
      if (this.sliderResizeTimer) {
        window.clearTimeout(this.sliderResizeTimer);
      }

      this.sliderResizeTimer = window.setTimeout(() => {
        this.sliderResizeTimer = null;

        if (this.flickity && typeof this.flickity.resize === "function") {
          this.flickity.resize();
        }
      }, 120);
    }

    observeChildren() {
      if (this.observer) {
        return;
      }

      this.observer = new MutationObserver((mutations) => {
        if (this.isUpdating) {
          return;
        }

        var shouldRefresh = mutations.some((mutation) => {
          var nodes = Array.from(mutation.addedNodes).concat(
            Array.from(mutation.removedNodes)
          );
          return nodes.some((node) => {
            if (node.nodeType !== 1) {
              return false;
            }

            if (node.matches && node.matches("remote-product-card")) {
              return true;
            }

            return Boolean(
              node.querySelector && node.querySelector("remote-product-card")
            );
          });
        });

        if (shouldRefresh) {
          this.refreshSlides();
        }
      });

      this.observer.observe(this, { childList: true });
    }

    refreshSlides() {
      if (!this.slideshow) {
        return;
      }

      var cards = Array.from(this.querySelectorAll("remote-product-card"));
      var controlType =
        this.getAttribute("controls") || this.dataset.controls || "dots";

      if (!cards.length) {
        if (this.flickity && typeof this.flickity.destroy === "function") {
          this.flickity.destroy();
          this.flickity = null;
        }

        this.slideshow.innerHTML = "";
        this.totalCardCount = 0;
        this.loadedCardCount = 0;
        return;
      }

      this.isUpdating = true;
      this.slideshow.innerHTML = "";
      var fragment = document.createDocumentFragment();
      var host = this.closest(".dy-campaign__target");
      var appSwatches = host && host.dataset.dySwatches === "true";
      cards.forEach((card) => {
        // Variant King (StarApps) decorates any node matching its
        // .boost-sd__product-item root selector with colorway swatches —
        // borrowing the class opts our cards into the same pipeline the
        // Boost carousel uses.
        if (appSwatches) {
          card.classList.add("boost-sd__product-item");
        }
        fragment.appendChild(card);
      });
      this.slideshow.appendChild(fragment);
      this.isUpdating = false;

      this.slideshow.dataset.controls = controlType;
      this.slideshow.dataset.count = cards.length;
      this.totalCardCount = cards.length;
      this.loadedCardCount = 0;

      cards.forEach((card) => {
        card.__dyCardLoaded = card.children.length > 0;
        if (card.__dyCardLoaded) {
          this.loadedCardCount += 1;
        }
      });

      this.setupSlider();

      if (this.loadedCardCount >= this.totalCardCount) {
        this.scheduleReinit(0);
      }
    }

    setupSlider() {
      var controlType = this.slideshow.dataset.controls;
      var count = parseFloat(this.slideshow.dataset.count);

      var prevNextButtons = false;
      var pageDots = true;

      if (controlType === "arrows") {
        pageDots = false;
        prevNextButtons = true;
      } else if (controlType === "arrows-dots") {
        pageDots = true;
        prevNextButtons = true;
      }

      if (this.flickity && typeof this.flickity.destroy === "function") {
        this.flickity.destroy();
        this.flickity = null;
      }

      if (window.theme && typeof theme.Slideshow === "function" && count > 1) {
        this.flickity = new theme.Slideshow(this.slideshow, {
          prevNextButtons: prevNextButtons,
          pageDots: pageDots,
          adaptiveHeight: false,
          groupCells: true,
          cellAlign: "left",
          contain: true,
          wrapAround: false,
        });
      }
    }

    onRemoteCardLoaded(event) {
      var card =
        event.target && event.target.closest
          ? event.target.closest("remote-product-card")
          : null;

      if (card) {
        this.hoistQuickShopModals(card);
      }

      if (card && !card.__dyCardLoaded) {
        card.__dyCardLoaded = true;
        this.loadedCardCount += 1;
      }

      if (this.loadedCardCount >= this.totalCardCount) {
        this.scheduleReinit(0);
      } else {
        this.scheduleReinit(180);
      }
    }

    scheduleReinit(delay) {
      if (this.reinitTimer) {
        window.clearTimeout(this.reinitTimer);
      }

      this.reinitTimer = window.setTimeout(() => {
        this.reinitTimer = null;

        if (this.flickity && typeof this.flickity.resize === "function") {
          this.flickity.resize();
        }

        if (window.theme && typeof theme.reinitProductGridItem === "function") {
          theme.reinitProductGridItem(this);
        }

        if (
          window.yotpoWidgetsContainer &&
          typeof window.yotpoWidgetsContainer.initWidgets === "function"
        ) {
          window.yotpoWidgetsContainer.initWidgets();
        } else if (
          window.yotpo &&
          typeof window.yotpo.refreshWidgets === "function"
        ) {
          window.yotpo.refreshWidgets();
        }

        // Prompt Variant King (StarApps combined listings) to decorate the
        // freshly injected cards with colorway swatches. No-op until the
        // app's selector config includes cards inside .dy-campaign.
        document.dispatchEvent(new CustomEvent("vkcl:product:refresh"));

        document.dispatchEvent(
          new CustomEvent("recommendations:loaded", {
            detail: {
              section: this,
              intent: "dynamic-yield",
            },
          })
        );
      }, delay || 0);
    }

    hoistQuickShopModals(scope) {
      if (!scope || !document.body) {
        return;
      }

      var modals = Array.from(scope.querySelectorAll(".modal--quick-shop[id]"));
      if (!modals.length) {
        return;
      }
      var hoisted = window.__dyQuickShopModalStore;
      if (!hoisted) {
        hoisted = window.__dyQuickShopModalStore = new Map();
      }

      modals.forEach((modal) => {
        var existing = hoisted.get(modal.id);

        // If we already hoisted a modal with this id, remove duplicates.
        if (existing && existing !== modal) {
          modal.remove();
          return;
        }

        if (modal.dataset.dyHoisted === "true") {
          hoisted.set(modal.id, modal);
          return;
        }

        modal.dataset.dyHoisted = "true";
        document.body.appendChild(modal);
        hoisted.set(modal.id, modal);
      });
    }
  }

  if (!customElements.get("dy-recommendation")) {
    customElements.define("dy-recommendation", DyRecommendation);
  }
})();
